<template>
  <div class="chunk-row">
    <ToolStatusIcon :status="status" />

    <div class="step-content">
      <div class="step-text">
        <div v-if="title || decision" class="tool-step-name">
          <span>{{ title }}</span>
          <span v-if="decision" class="tool-step-decision">{{ decisionLabel }}</span>
        </div>
        <span
          v-for="(part, idx) in textParts"
          :key="idx"
          class="step-part"
          :class="{ 'tool-error-text': part.isError }"
          v-html="formatContent(part.text)"
        />
      </div>
      <div v-for="(items, listIdx) in itemLists" :key="`items-${listIdx}`" class="tool-items-block">
        <div class="tool-items-title">Items:</div>
        <ul class="tool-items-list">
          <li v-for="(item, idx) in items" :key="idx" class="tool-item-row">
            <pre>{{ JSON.stringify(item, null, 2) }}</pre>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, defineProps } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  APPROVAL_DECISION,
  TOOL_CALL_TYPE,
  TOOL_STEP_HIDDEN_INPUT_LABELS,
  TOOL_STEP_STATUS,
} from '~/constants/conversationModels';
import { formatKey } from '~/utils/format';
import { getDecisionLabel } from '~/utils/step-label';

import ToolStatusIcon from './ToolStatusIcon.vue';

const props = defineProps({
  events: { type: Array, default: () => [] },
  isLoading: { type: Boolean, default: false },
  formatContent: { type: Function, required: true },
  title: { type: String, default: '' },
  decision: { type: String, default: '' },
});

const { t } = useI18n();

const isErrorEvent = (event) => event.data?.event === TOOL_CALL_TYPE.TOOL_ERROR;

const isError = computed(() => props.events.some(isErrorEvent));

const status = computed(() => {
  if (isError.value || props.decision === APPROVAL_DECISION.REJECT) return TOOL_STEP_STATUS.ERROR;
  return props.isLoading ? TOOL_STEP_STATUS.RUNNING : TOOL_STEP_STATUS.DONE;
});

const decisionLabel = computed(() => getDecisionLabel(props.decision, t));

const getInputSummary = (data) => {
  const input = data?.input;
  if (!input || typeof input !== 'object' || Array.isArray(input)) return 'Called';
  const entries = Object.entries(input)
    .map(([k, v]) => [formatKey(k), v])
    .filter(([label]) => !TOOL_STEP_HIDDEN_INPUT_LABELS.includes(label));
  if (!entries.length) return 'Called';
  return entries.map(([label, v]) => `${label}: ${typeof v === 'object' ? JSON.stringify(v) : v}`).join('\n\n');
};

const getToolResultSummary = (data) => {
  const artifact = data?.output?.artifact;
  if (artifact === undefined || artifact === null) return 'Success';
  if (Array.isArray(artifact)) return `${artifact.length} results`;
  if (typeof artifact === 'object') return `${Object.keys(artifact).length} fields`;
  return `Result: ${artifact}`;
};

// Each event adds its text in order, rendered inline: "Query: … Called 20 results". An answered approval
// shows only the requested arguments, since its decision sits next to the title.
const textParts = computed(() => {
  if (props.decision) {
    return props.events
      .map((event) => getInputSummary(event.data))
      .filter((summary) => summary !== 'Called')
      .map((summary) => ({ text: summary }));
  }

  return props.events.flatMap((event) => {
    const data = event.data;
    if (isErrorEvent(event)) return [{ text: data.message || getInputSummary(data), isError: true }];

    return [
      { text: getInputSummary(data) },
      ...(data?.output ? [{ text: getToolResultSummary(data) }] : []),
      ...(data?.message ? [{ text: data.message }] : []),
    ];
  });
});

const itemLists = computed(() =>
  props.events.map((event) => event.data?.items).filter((items) => Array.isArray(items) && items.length),
);
</script>

<style scoped lang="scss">
.chunk-row {
  display: flex;
  align-items: flex-start;
  gap: $bb-spacing-2;
}

.step-content {
  flex: 1 1 auto;
  min-width: 0;
}

.step-text {
  font-size: 13px;
  line-height: 16px;
  letter-spacing: 0.1px;
  color: $bb-neutral-1;
  word-break: break-word;

  :deep(p) {
    margin: 0;
  }
}
.tool-error-text {
  color: $bb-interface-5 !important;
}

.tool-step-name {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: $bb-spacing-2;
  font-weight: 700;
}
.tool-step-decision {
  flex-shrink: 0;
  font-family: monospace;
  font-size: 10px;
  font-weight: 400;
  letter-spacing: 0;
  color: #6a6765;
}

.step-part {
  :deep(p) {
    display: inline;
  }
  & + &::before {
    content: ' ';
  }
}

.tool-items-block {
  background: #f7f7fa;
  border-radius: 8px;
  padding: 8px 12px;
  margin: 8px 0;
  .tool-items-title {
    font-weight: 600;
    margin-bottom: 4px;
    color: #4b4b4b;
    font-size: 13px;
  }
  .tool-items-list {
    list-style: disc inside;
    margin: 0;
    padding: 0 0 0 12px;
  }
  .tool-item-row {
    font-size: 12px;
    color: #333;
    margin-bottom: 2px;
  }
}
</style>
